import { useBlocker } from '@buzola/router'
import { useBindxContext, usePersist } from '@contember/bindx-react'
import { useCallback, useEffect, useRef } from 'react'
import { confirmDialog } from './confirm-dialog'

const unsavedChangesQuestion = 'Leave without saving?'
const unsavedChangesDescription = 'The changes you made on this page will be discarded.'

export function UnsavedChangesGuard() {
	const { isDirty } = usePersist()
	const { store, batchPersister } = useBindxContext()
	// Block every SPA navigation and decide at block time whether there is
	// anything dirty. Reading the ChangeRegistry on demand keeps the guard
	// immune to a stale reactive `isDirty` — a stale `false` here silently
	// discarded the user's edits, e.g. when leaving the course form via
	// "Add person".
	const { state, proceed, reset } = useBlocker(true)
	const isPromptingRef = useRef(false)
	// Buzola's blocker never learns the destination, so we listen to the same
	// Navigation API event it intercepts (dispatched synchronously, before the
	// URL commits) and remember where the navigation goes. A same-path
	// navigation — `?locale=cs` from LocaleTabs, a grid's `?page=` — keeps
	// the page (and bindx's dirty state) mounted, so it must never prompt.
	const pendingNavigationRef = useRef<{ fromPath: string; toPath: string } | null>(null)
	useEffect(() => {
		const navigation = window.navigation
		if (!navigation) return
		const remember = (event: NavigateEvent) => {
			pendingNavigationRef.current = {
				fromPath: window.location.pathname,
				toPath: new URL(event.destination.url).pathname,
			}
		}
		navigation.addEventListener('navigate', remember)
		return () => navigation.removeEventListener('navigate', remember)
	}, [])

	const getDirtyEntities = useCallback(
		() => batchPersister?.getChangeRegistry().getDirtyEntities() ?? [],
		[batchPersister],
	)

	// Mounting a form dirties the store all by itself: `SeedDefaults` writes model
	// defaults, `AutoDefaultFieldValue` fills a column, and `RichTextField` creates
	// an empty Content document for every empty rich-text relation — all in effects,
	// none of them the user's doing. Prompting on those meant "unsaved changes?" on
	// the way out of a record nobody had touched.
	//
	// A baseline taken shortly after the navigation does not fix it: the page's data
	// arrives asynchronously, so the seeding runs a second or two later and lands
	// outside the baseline. Measured on /community/team/edit: navigation, baseline
	// captured empty, then ~2 s later `RichTextField` seeds the `bio` Content and
	// leaving prompts.
	//
	// So the baseline is not taken on a timer at all — it is frozen at the user's
	// first real interaction with the page. Everything the app seeded before the
	// user touched anything is part of the baseline by construction, whenever it
	// happened. And a page the user never touched cannot hold their unsaved work,
	// so it never prompts.
	const fingerprintDirty = useCallback(() => {
		const map = new Map<string, string>()
		for (const entity of getDirtyEntities()) {
			// A create with no dirty field and no dirty relation is an empty seeded
			// row (the Content document above). It holds nothing to lose — the moment
			// the user types into it, it gains a dirty field and counts again.
			if (entity.changeType === 'create' && entity.dirtyFields.length === 0 && entity.dirtyRelations.length === 0) {
				continue
			}
			const snapshot = store.getEntitySnapshot(entity.entityType, entity.entityId)
			map.set(`${entity.entityType}:${entity.entityId}`, JSON.stringify(snapshot?.data ?? null))
		}
		return map
	}, [getDirtyEntities, store])

	const baselineRef = useRef<Map<string, string>>(new Map())
	const hasInteractedRef = useRef(false)

	// Capture phase on window, so the baseline is frozen before React handles the
	// event and before any value changes. `pointerdown` covers the clicks that
	// mutate without a form control — "Add reason", the row trash icon, drag to
	// reorder — which is why the gate cannot be narrowed to `input`/`change`:
	// missing one of those would drop a real edit silently.
	useEffect(() => {
		const freeze = (event: Event) => {
			if (!event.isTrusted || hasInteractedRef.current) return
			hasInteractedRef.current = true
			baselineRef.current = fingerprintDirty()
		}
		const events = ['pointerdown', 'keydown', 'drop', 'paste'] as const
		for (const type of events) window.addEventListener(type, freeze, true)
		return () => {
			for (const type of events) window.removeEventListener(type, freeze, true)
		}
	}, [fingerprintDirty])

	// Only a navigation that changes the path unmounts the form and starts a fresh
	// page. `?locale=cs` from LocaleTabs keeps it mounted together with the user's
	// unsaved edits, so re-arming there would swallow them into a new baseline.
	useEffect(() => {
		const navigation = window.navigation
		if (!navigation) return
		const onNavigate = () => {
			const pending = pendingNavigationRef.current
			if (pending !== null && pending.fromPath === pending.toPath) return
			hasInteractedRef.current = false
			baselineRef.current = new Map()
		}
		navigation.addEventListener('navigatesuccess', onNavigate)
		return () => navigation.removeEventListener('navigatesuccess', onNavigate)
	}, [])

	const hasUserChanges = useCallback(() => {
		if (!hasInteractedRef.current) return false
		const baseline = baselineRef.current
		for (const [key, fingerprint] of fingerprintDirty()) {
			if (baseline.get(key) !== fingerprint) return true
		}
		return false
	}, [fingerprintDirty])

	const discardUnsavedChanges = useCallback(() => {
		for (const { entityType, entityId, changeType } of getDirtyEntities()) {
			if (changeType === 'create') {
				store.resetAllRelations(entityType, entityId)
				store.removeEntity(entityType, entityId)
				continue
			}

			if (changeType === 'delete') {
				store.unscheduleForDeletion(entityType, entityId)
			}

			store.resetEntity(entityType, entityId)
			store.resetAllRelations(entityType, entityId)
		}

		store.notify()
	}, [store, getDirtyEntities])

	useEffect(() => {
		if (!isDirty) return

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (!hasUserChanges()) return
			event.preventDefault()
			event.returnValue = ''
		}

		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [isDirty, hasUserChanges])

	useEffect(() => {
		if (state !== 'blocked') {
			isPromptingRef.current = false
			return
		}
		if (isPromptingRef.current) return

		// Nothing dirty, or the page stays mounted → let the navigation through
		// without bothering the user (and without discarding anything).
		const pending = pendingNavigationRef.current
		const staysOnPage = pending !== null && pending.fromPath === pending.toPath
		if (staysOnPage || !hasUserChanges()) {
			proceed()
			return
		}

		// The navigation stays blocked while the dialog is open — that is what a
		// blocker is for — and `isPromptingRef` keeps the effect from asking twice.
		isPromptingRef.current = true
		void confirmDialog({
			cancelLabel: 'Stay on the page',
			confirmLabel: 'Discard changes',
			description: unsavedChangesDescription,
			title: unsavedChangesQuestion,
			tone: 'danger',
		}).then(discard => {
			if (discard) {
				discardUnsavedChanges()
				proceed()
				return
			}
			reset()
		})
	}, [state, proceed, reset, discardUnsavedChanges, hasUserChanges])

	return null
}
